import { TextField } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getListCategory } from "../../services/purchaseApi";
import { useQuery } from "@tanstack/react-query";

const SwipeFilterBy = ({ open, onClose, reqBody, setReqBody }) => {
  const [isDesktop, setIsDesktop] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    reqBody.category_id || 0,
  );
  const [salaryRange, setSalaryRange] = useState({
    min: "",
    max: "",
  });
  const [bodyPrice, setBodyPrice] = useState({
    lower_price: reqBody.lower_price || 0,
    higher_price: reqBody.higher_price || 0,
  });
  const [priceError, setPriceError] = useState("");

  const { data: dataListCategory, isFetching: fetchingCategory } = useQuery({
    queryKey: ["category-list"],
    queryFn: () => getListCategory(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const fn = () => setIsDesktop(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedCategory(reqBody.category_id || 0);
    setBodyPrice({
      lower_price: reqBody.lower_price || 0,
      higher_price: reqBody.higher_price || 0,
    });
    setSalaryRange({
      min:
        reqBody.lower_price && reqBody.lower_price > 0
          ? String(reqBody.lower_price)
          : "",
      max:
        reqBody.higher_price && reqBody.higher_price > 0
          ? String(reqBody.higher_price)
          : "",
    });
    setPriceError("");
  }, [open]);

  const formatCurrency = (amount) => {
    return amount ? Number(amount).toLocaleString("id-ID") : 0;
  };

  const handleSalaryRangeChange = (field, value) => {
    const numericValue = value.replace(/\D/g, "");
    const newRange = {
      ...salaryRange,
      [field]: numericValue,
    };

    setSalaryRange(newRange);

    if (
      newRange.min &&
      newRange.max &&
      parseInt(newRange.min, 10) > parseInt(newRange.max, 10)
    ) {
      setPriceError("Harga tertinggi harus lebih besar dari harga terendah");
    } else {
      setPriceError("");
    }

    if (
      newRange.min &&
      newRange.max &&
      parseInt(newRange.min, 10) <= parseInt(newRange.max, 10)
    ) {
      setBodyPrice({
        lower_price: parseInt(newRange.min, 10),
        higher_price: parseInt(newRange.max, 10),
      });
    }
  };

  const handleConfirm = () => {
    setReqBody({
      ...reqBody,
      category_id: selectedCategory,
      lower_price: bodyPrice.lower_price,
      higher_price: bodyPrice.higher_price,
    });
    onClose();
  };

  const handleReset = () => {
    setSelectedCategory(0);
    setSalaryRange({ min: "", max: "" });
    setBodyPrice({ lower_price: 0, higher_price: 0 });
    setPriceError("");
    setReqBody({
      ...reqBody,
      category_id: 0,
      lower_price: 0,
      higher_price: 0,
    });
    onClose();
  };

  const panelClass = isDesktop
    ? "fixed left-1/2 top-1/2 z-[9999] flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white shadow-2xl text-base"
    : "fixed bottom-0 left-0 right-0 z-[9999] flex max-h-[70vh] flex-col rounded-t-3xl bg-white shadow-2xl text-base";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black bg-opacity-50"
            onClick={onClose}
          />

          <motion.div
            initial={
              isDesktop ? { opacity: 0, scale: 0.96 } : { y: "100%" }
            }
            animate={isDesktop ? { opacity: 1, scale: 1 } : { y: 0 }}
            exit={isDesktop ? { opacity: 0, scale: 0.96 } : { y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className={panelClass}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-2 border-b border-slate-100 px-3 py-2 md:flex-row md:items-center md:justify-between md:px-5 md:py-4">
              {!isDesktop && (
                <div className="flex w-full justify-center">
                  <div className="h-1.5 w-[25%] rounded-full bg-gray-200" />
                </div>
              )}
              <h2 className="text-lg font-semibold text-slate-900">Filter</h2>
              {isDesktop && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                  aria-label="Tutup"
                >
                  <IoClose size={22} />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3 md:px-5 md:pb-2">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-800">Kategori</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {dataListCategory && !fetchingCategory ? (
                      dataListCategory.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => setSelectedCategory(category.id)}
                          className={`cursor-pointer rounded-full border px-3 py-2 text-md transition-colors ${
                            selectedCategory === category.id
                              ? "border-secondary bg-secondaryActive text-secondary"
                              : "border-slate-100 bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {category.name}
                        </button>
                      ))
                    ) : fetchingCategory ? (
                      <>
                        {[1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="w-1/4 min-w-[4rem] animate-pulse rounded-full border border-slate-100 bg-slate-100 py-2 px-3 text-md text-slate-100"
                          >
                            -
                          </div>
                        ))}
                      </>
                    ) : null}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-slate-800">Harga</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="text-md">Terendah</p>
                        <TextField
                          placeholder="Rp0"
                          fullWidth
                          sx={{
                            width: "100%",
                            backgroundColor: "#FFF",
                            fontSize: "14px",
                            height: "42px",
                            fontFamily: '"Montserrat", sans-serif',
                            borderRadius: "8px",
                            ".MuiInputBase-root": {
                              height: "42px",
                              fontSize: "14px",
                              fontFamily: '"Montserrat", sans-serif',
                            },
                            ".MuiAutocomplete-input": {
                              padding: "0 14px",
                            },
                            ".MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderRadius: "8px",
                              },
                            },
                            ".MuiFormHelperText-root": {
                              color: "red",
                              fontSize: "12px",
                              textAlign: "right",
                            },
                          }}
                          value={
                            salaryRange.min
                              ? `Rp${formatCurrency(parseInt(salaryRange.min, 10))}`
                              : ""
                          }
                          onChange={(e) =>
                            handleSalaryRangeChange("min", e.target.value)
                          }
                          error={!!priceError}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <p className="text-md">Tertinggi</p>
                        <TextField
                          placeholder="Rp0"
                          fullWidth
                          sx={{
                            width: "100%",
                            backgroundColor: "#FFF",
                            fontSize: "14px",
                            height: "42px",
                            fontFamily: '"Montserrat", sans-serif',
                            borderRadius: "8px",
                            ".MuiInputBase-root": {
                              height: "42px",
                              fontSize: "14px",
                              fontFamily: '"Montserrat", sans-serif',
                            },
                            ".MuiAutocomplete-input": {
                              padding: "0 14px",
                            },
                            ".MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderRadius: "8px",
                              },
                            },
                            ".MuiFormHelperText-root": {
                              color: "red",
                              fontSize: "12px",
                              textAlign: "right",
                            },
                          }}
                          value={
                            salaryRange.max
                              ? `Rp${formatCurrency(parseInt(salaryRange.max, 10))}`
                              : ""
                          }
                          onChange={(e) =>
                            handleSalaryRangeChange("max", e.target.value)
                          }
                          error={!!priceError}
                        />
                      </div>
                    </div>
                    {priceError && (
                      <p className="w-full text-sm italic text-error">
                        {priceError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2 rounded-t-3xl border-t border-slate-100 bg-white px-3 py-4 md:rounded-b-2xl md:px-5">
              <button
                type="button"
                onClick={handleReset}
                className="h-[40px] w-full rounded-lg border border-secondary bg-white font-medium tracking-wide text-secondary hover:bg-secondaryActive focus:outline-none focus:shadow-outline"
              >
                Atur Ulang
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="h-[40px] w-full rounded-lg border border-secondary bg-secondary font-medium tracking-wide text-white hover:bg-secondaryActive focus:outline-none focus:shadow-outline"
              >
                Terapkan
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SwipeFilterBy;
